// src/main.tsx
import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useNavigate,
  isRouteErrorResponse,
  useRouteError,
} from 'react-router-dom'
import { supabase } from './lib/supabaseClient'

// Estilos globales
import './App.css'
import './styles/globals.css'

// Páginas
import Admin from './pages/Home'
import PublicList from './pages/PublicList'
import Login from './pages/Login'

// Slug fijo para toda la app
import { FIXED_SLUG } from './config'

/** -------- ErrorBoundary simple -------- */
function RouteError() {
  const err = useRouteError() as any
  const msg =
    (isRouteErrorResponse(err) && `${err.status} ${err.statusText}`) ||
    err?.message ||
    String(err)
  const stack = err?.stack
  return (
    <div style={{ padding: 16 }}>
      <h2>Ocurrió un error en esta ruta</h2>
      <pre style={{ whiteSpace: 'pre-wrap' }}>{msg}</pre>
      {stack && (
        <details>
          <summary>Stack</summary>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{stack}</pre>
        </details>
      )}
    </div>
  )
}

/** -------- Ruta protegida por sesión Supabase -------- */
function Protected({ children }: { children: React.ReactNode }) {
  const [ok, setOk] = React.useState<boolean | null>(null)

  React.useEffect(() => {
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setOk(!!data.session)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setOk(!!session)
    })
    return () => {
      active = false
      try {
        // compat con SDKs viejos/nuevos
        // @ts-ignore
        sub?.subscription?.unsubscribe?.()
        // @ts-ignore
        sub?.unsubscribe?.()
      } catch {}
    }
  }, [])

  if (ok === null) return null
  return ok ? <>{children}</> : <Navigate to="/login" replace />
}

/** -------- Redirección desde "/" a la lista fija -------- */
function DefaultListRedirect() {
  const nav = useNavigate()
  React.useEffect(() => {
    nav(`/l/${FIXED_SLUG}`, { replace: true })
  }, [nav])
  return null
}

/** -------- Router --------
 *  Mantengo /l/:slug por compat (el componente PublicList ya ignora
 *  el param y usa FIXED_SLUG internamente).
 */
const router = createBrowserRouter(
  [
    { path: '/l/:slug', element: <PublicList />, errorElement: <RouteError /> },
    { path: '/l', element: <DefaultListRedirect />, errorElement: <RouteError /> },
    { path: '/login', element: <Login />, errorElement: <RouteError /> },
    {
      path: '/admin',
      element: (
        <Protected>
          <Admin />
        </Protected>
      ),
      errorElement: <RouteError />,
    },
    { path: '/', element: <DefaultListRedirect />, errorElement: <RouteError /> },
    { path: '*', element: <DefaultListRedirect />, errorElement: <RouteError /> },
  ],
  {
    // Debe coincidir con vite.config (base)
    basename: import.meta.env.BASE_URL,
  }
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
)
